import { useCallback, useState } from 'react';
import type { Exercise } from '@heraion/shared';

import type { DurableWriteFn, NotifyFn } from '../seams';
import { saveExerciseTimerDefaults } from './exerciseTimerDefaults';
import {
  defaultRestSecFor,
  REST_FALLBACK_SEC,
  REST_PICKER_MIN,
  REST_PICKER_MAX,
} from '@heraion/shared';

// Owns the in-Logger rest-default picker (1.82.0 slider Modal). Extracted
// verbatim out of LoggerScreen (Track R) so the screen just wires the
// returned state + handlers into the rest banner button and
// RestDefaultPicker. State lives here (not in the banner) so the Modal
// can render outside the banner's conditional-on-running gate — the
// timer can finish mid-picker and we don't want the picker to vanish
// under the user's finger.
type UseRestPickerArgs = {
  catalog: Exercise[] | null;
  setCatalog: React.Dispatch<React.SetStateAction<Exercise[] | null>>;
  /** Profile.defaultRestSec, used as the seed fallback for exercises
   *  that predate any per-exercise tuning. */
  profileDefaultRestSec: number | null | undefined;
  /** Re-pull the profile after a global-default save. */
  refreshProfile: () => Promise<unknown> | void;
  /** The exercise the running rest timer is counting for; the picker
   *  opens against it. Null when no timer is active. */
  activeRestExerciseId: string | null;
  t: (k: string, opts?: Record<string, unknown>) => string;
  durableWrite: DurableWriteFn;
  /** Get-merge-PATCH the host's Profile (mobile: patchProfile; web-rnw:
   *  its own Profile-doc write). */
  patchProfile: (patch: { defaultRestSec: number }) => Promise<void>;
  notify: NotifyFn;
  /** Marks the host's catalog source stale (#342). */
  onCatalogSaved?: () => void;
};

export function useRestPicker({
  catalog,
  setCatalog,
  profileDefaultRestSec,
  refreshProfile,
  activeRestExerciseId,
  t,
  durableWrite,
  patchProfile,
  notify,
  onCatalogSaved,
}: UseRestPickerArgs) {
  const [savingDefaultRest, setSavingDefaultRest] = useState(false);
  const [restPickerOpen, setRestPickerOpen] = useState(false);
  const [restPickerSlug, setRestPickerSlug] = useState<string | null>(null);
  const [restPickerValue, setRestPickerValue] =
    useState<number>(REST_FALLBACK_SEC);
  // Inbox ddac68ab: when checked, the modal saves to Profile.defaultRestSec
  // instead of Exercise.defaultRestSec. Default off so the existing
  // per-exercise flow stays one tap. Reset every time the modal closes
  // so a previous "global" pick doesn't accidentally carry over.
  const [restPickerSaveAsGlobal, setRestPickerSaveAsGlobal] =
    useState<boolean>(false);

  const applyDefaultRest = useCallback(
    async (slug: string, seconds: number) => {
      setSavingDefaultRest(true);
      try {
        // The same write as the timer sheet and the Settings card (#411):
        // the athlete's own row, the rest only (the hold stays as it is),
        // and a rest equal to the global clears the override instead of
        // storing it. A queued write keeps the local catalog edit and
        // returns true: blocking the athlete's rest default behind the
        // network would be the DX-2 bug in miniature. An exercise missing
        // from the catalog throws too: the error below, never a silent true.
        await saveExerciseTimerDefaults({
          slug,
          restSec: seconds,
          restGlobal: profileDefaultRestSec ?? REST_FALLBACK_SEC,
          catalog,
          setCatalog,
          durableWrite,
          onSaved: onCatalogSaved,
        });
        return true;
      } catch {
        notify(
          t('mobile.logger.restEditTitle'),
          t('mobile.logger.restEditError'),
        );
        return false;
      } finally {
        setSavingDefaultRest(false);
      }
    },
    [catalog, setCatalog, profileDefaultRestSec, t, durableWrite, onCatalogSaved, notify],
  );
  // Sibling of applyDefaultRest that targets Profile.defaultRestSec
  // instead of Exercise.defaultRestSec. Used by the in-Logger slider
  // modal when the "Use as global default" checkbox is on. The full-doc
  // round-trip it used to spell out by hand now lives in patchProfile —
  // the mobile snapshot is still too narrow to rebuild a PUT body locally,
  // which is exactly why that round-trip has one home instead of eight.
  const applyGlobalDefaultRest = useCallback(
    async (seconds: number): Promise<boolean> => {
      setSavingDefaultRest(true);
      try {
        await patchProfile({ defaultRestSec: seconds });
        await refreshProfile();
        return true;
      } catch {
        notify(
          t('mobile.logger.restEditTitle'),
          t('mobile.logger.restEditError'),
        );
        return false;
      } finally {
        setSavingDefaultRest(false);
      }
    },
    [refreshProfile, t, patchProfile, notify],
  );

  const open = useCallback(() => {
    const slug = activeRestExerciseId;
    if (!slug) return;
    const ex = catalog?.find((x) => x.slug === slug);
    setRestPickerSlug(slug);
    // Seed from the catalog's stored default; fall back to the profile
    // global default when the row predates any per-exercise tuning.
    // Clamp into the slider domain (REST_PICKER_MIN..REST_PICKER_MAX)
    // so the thumb never starts off-track on legacy outliers.
    const seed = defaultRestSecFor(ex?.defaultRestSec, profileDefaultRestSec);
    setRestPickerValue(
      Math.max(REST_PICKER_MIN, Math.min(REST_PICKER_MAX, seed)),
    );
    setRestPickerOpen(true);
  }, [activeRestExerciseId, catalog, profileDefaultRestSec]);

  const cancel = useCallback(() => {
    setRestPickerOpen(false);
    setRestPickerSaveAsGlobal(false);
  }, []);

  const toggleSaveAsGlobal = useCallback(() => {
    setRestPickerSaveAsGlobal((v) => !v);
  }, []);

  const confirm = useCallback(() => {
    void (async () => {
      const ok = restPickerSaveAsGlobal
        ? await applyGlobalDefaultRest(restPickerValue)
        : restPickerSlug
          ? await applyDefaultRest(restPickerSlug, restPickerValue)
          : false;
      if (ok) {
        setRestPickerOpen(false);
        setRestPickerSaveAsGlobal(false);
      }
    })();
  }, [
    restPickerSaveAsGlobal,
    restPickerValue,
    restPickerSlug,
    applyGlobalDefaultRest,
    applyDefaultRest,
  ]);

  return {
    saving: savingDefaultRest,
    visible: restPickerOpen,
    slug: restPickerSlug,
    value: restPickerValue,
    saveAsGlobal: restPickerSaveAsGlobal,
    setValue: setRestPickerValue,
    open,
    cancel,
    toggleSaveAsGlobal,
    confirm,
  };
}
